import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { RenderProgress } from "@/components/render/RenderProgress";
import { useRenderByIdQuery, downloadRender } from "@/api/renders";

interface RenderDetailPageProps {
  renderId: string;
}

export function RenderDetailPage({ renderId }: RenderDetailPageProps) {
  const { data: job, isLoading } = useRenderByIdQuery(renderId, true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function handleDownload() {
    if (!job) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const ext = job.format === "prores" ? "mov" : job.format === "h265" ? "mp4" : job.format;
      await downloadRender(job.id, `render-${job.id}.${ext}`);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-line border-t-accent" />
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <div className="py-16 text-center text-sm text-ink-2">
          Render job not found.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-3xl p-6">
        <div className="mb-6">
          <Link
            to="/renders"
            className="text-sm text-ink-2 hover:text-ink"
          >
            &larr; Back to Renders
          </Link>
        </div>

        <h1 className="mb-2 text-2xl font-bold text-ink">
          Render: {job.template_name ?? job.template_id}
        </h1>

        <div className="mb-6 flex items-center gap-4">
          <span className="text-xs font-medium uppercase text-ink-2">
            {job.format}
          </span>
          <span className="text-xs text-ink-3">
            {new Date(job.created_at).toLocaleString()}
          </span>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <RenderProgress jobId={job.id} />
        </div>

        {/* Details */}
        <div className="rounded-xl border border-line bg-surface-2">
          <div className="border-b border-line px-4 py-2">
            <span className="text-sm font-medium text-ink-2">
              Job Details
            </span>
          </div>
          <div className="space-y-3 p-4">
            <DetailRow label="Job ID" value={job.id} />
            <DetailRow label="Template" value={job.template_name ?? job.template_id} />
            <DetailRow label="Format" value={job.format.toUpperCase()} />
            <DetailRow label="Status" value={job.status} />
            {job.output_size != null && (
              <DetailRow
                label="File Size"
                value={`${(job.output_size / (1024 * 1024)).toFixed(1)} MB`}
              />
            )}
          </div>
        </div>

        {/* Input Props */}
        <div className="mt-4 rounded-xl border border-line bg-surface-2">
          <div className="border-b border-line px-4 py-2">
            <span className="text-sm font-medium text-ink-2">
              Input Props
            </span>
          </div>
          <pre className="overflow-x-auto p-4 font-mono text-xs text-ink-2">
            {(() => {
              try {
                return JSON.stringify(JSON.parse(job.props), null, 2);
              } catch {
                return job.props;
              }
            })()}
          </pre>
        </div>

        {/* Download */}
        {job.status === "complete" && (
          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-block rounded-lg bg-accent px-6 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
            >
              {downloading ? "Downloading..." : `Download ${job.format.toUpperCase()}`}
            </button>
            {downloadError && (
              <span className="text-xs text-danger">{downloadError}</span>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-ink-2">{label}</span>
      <span className="text-sm text-ink">{value}</span>
    </div>
  );
}
